import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CartPage.css';

const CartPage = ({ cartItems, updateQuantity, removeFromCart, subtotal, user }) => {
  const navigate = useNavigate();

  // --- 1. CALCULATIONS ---
  // Uses the subtotal passed from App.js, but has a fallback calculation for safety
  const displaySubtotal = useMemo(() => {
    if (subtotal > 0) return subtotal;
    return cartItems.reduce((acc, item) => {
      const p = Number(item.price) || 0;
      const q = Number(item.quantity) || 0;
      return acc + (p * q);
    }, 0);
  }, [cartItems, subtotal]);

  // --- 2. AUTHENTICATION GUARD ---
  useEffect(() => {
    const token = localStorage.getItem('user_token');
    
    // Redirect if no session exists in state or local storage
    if (!user && !token) {
      console.log("Session not found, redirecting to login...");
      navigate('/login');
    }
  }, [user, navigate]);

  // --- 3. FORMATTING HELPERS ---
  const formatPrice = (amount) => {
    const value = Number(amount) || 0;
    return `₹${value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    })}`;
  };

  // --- 4. RENDER GUARDS ---
  if (!user && localStorage.getItem('user_token')) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Verifying your session...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="cart-page-container">
      <div className="cart-header">
        <h2>Shopping Cart <span>({cartItems.length} items)</span></h2>
      </div>

      {cartItems.length === 0 ? (
        <div className="empty-cart-view">
          <div className="empty-cart-icon">🛒</div>
          <h3>Your cart is feeling a bit light!</h3>
          <p>Add some items to get started with your order.</p>
          <button className="shop-now-btn" onClick={() => navigate('/agro')}>
            Explore Products
          </button>
        </div>
      ) : (
        <div className="cart-main-content">
          {/* Left Side: Items List */}
          <div className="cart-items-section">
            {cartItems.map((item) => {
              const itemKey = `${item.id}-${item.selectedWeight}`;
              const price = Number(item.price) || 0;
              const qty = Number(item.quantity) || 0;
              const itemTotal = price * qty;

              return (
                <div key={itemKey} className="cart-item-card">
                  <div className="cart-item-img-box">
                    <img 
                      src={
                        item.image && item.image.startsWith('http') 
                          ? item.image 
                          : item.image 
                            ? `https://atrmart.in/storage/${item.image}`
                            : 'https://via.placeholder.com/150?text=No+Image'
                      } 
                      alt={item.name} 
                    />
                    {item.selectedWeight && (
                      <div className="weight-badge-overlay">
                        {item.selectedWeight}
                      </div>
                    )}
                  </div>

                  <div className="cart-item-info">
                    <h3 className="item-name">{item.name}</h3>
                    <p className="item-unit-price">Unit Price: {formatPrice(price)}</p>
                    
                    <div className="cart-item-actions">
                      <div className="quantity-control-group">
                        <button 
                          className="qty-action-btn minus"
                          onClick={() => updateQuantity(item.id, item.selectedWeight, qty - 1)}
                        >
                          −
                        </button>
                        <span className="qty-display-text">{qty}</span>
                        <button 
                          className="qty-action-btn plus"
                          onClick={() => updateQuantity(item.id, item.selectedWeight, qty + 1)}
                        >
                          +
                        </button>
                      </div>

                      <button 
                        className="item-remove-link" 
                        onClick={() => removeFromCart(item.id, item.selectedWeight)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="item-total-price-column">
                    {formatPrice(itemTotal)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Side: Summary Sidebar */}
          <div className="cart-summary-sidebar">
            <div className="summary-card">
              <h3>Order Summary</h3>
              <div className="summary-details">
                <div className="summary-row">
                  <span>Price ({cartItems.length} items)</span>
                  <span>{formatPrice(displaySubtotal)}</span>
                </div>
                <div className="summary-row">
                  <span>Delivery Charges</span>
                  <span className="free-text">- </span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-row total">
                  <span>Total Amount</span>
                  <span>{formatPrice(displaySubtotal)}</span>
                </div>
              </div>
              
              <button 
                className="checkout-proceed-btn" 
onClick={() => navigate('/checkout', { 
    state: { 
      cartItems: cartItems,
      subtotal: displaySubtotal,
      taxAmount: displaySubtotal * 0.05, // 5% GST calculation
      estimatedTotal: displaySubtotal * 1.05 // Subtotal + GST
    } 
  })}
  disabled={displaySubtotal <= 0}
>
  Proceed to Checkout
</button>
              
              <p className="safety-note">
                🛡️ Secure Payment & Data Protection
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;